import React, { useMemo, useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { buildTeamWeekly } from './CrewsWeeklyView';

const CHART_COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#14B8A6', '#F97316', '#0EA5E9', '#A855F7'];

const short = (iso) => { if (!iso) return ''; const p = String(iso).split('-'); return `${p[1]}/${p[2]}`; };
const full = (iso) => { if (!iso) return ''; const p = String(iso).split('-'); return `${p[1]}/${p[2]}/${p[0]}`; };

const sortTeamName = (a, b) => {
  const re = /^PM(\d+)$/;
  const ma = a.match(re), mb = b.match(re);
  if (ma && mb) return parseInt(ma[1]) - parseInt(mb[1]);
  if (ma) return -1; if (mb) return 1;
  return a.localeCompare(b);
};

// métricas possíveis e a qual campo do "Customizar Campos" (tabela) elas respondem
const METRICS = [
  { key: 'total_final', label: 'Total Final (PM)', money: true, field: 'total_final' },
  { key: 'lucro_empresa', label: 'Lucro (Empresa)', money: true, field: 'lucro' },
  { key: 'total_labor', label: 'Total Labor', money: true, field: 'total_labor' },
  { key: 'total_expenses', label: 'Despesas', money: true, field: 'despesas' },
  { key: 'backcharge_total', label: 'Backcharge', money: true, field: 'backcharge' },
  { key: 'trabalhos', label: 'Trabalhos', money: false, field: 'trabalhos' },
];

const CompareTeamsModal = ({
  open,
  onClose,
  teams = [],
  selectedTeams = [],
  weeksBreakdown = [],
  tableFields = {},
  isAdmin = false,
  formatMoney = (v) => v,
}) => {
  const availableMetrics = useMemo(() => {
    const list = METRICS.filter((m) => {
      if (m.money && !isAdmin) return false;
      return tableFields[m.field] !== false; // mostra a menos que explicitamente desmarcado
    });
    return list.length ? list : [{ key: 'trabalhos', label: 'Trabalhos', money: false, field: 'trabalhos' }];
  }, [tableFields, isAdmin]);

  const [metric, setMetric] = useState('total_final');
  useEffect(() => {
    if (!availableMetrics.find((m) => m.key === metric)) {
      setMetric(availableMetrics[0].key);
    }
  }, [availableMetrics, metric]);

  const weekMetaMap = useMemo(() => {
    const m = {};
    (weeksBreakdown || []).forEach((w) => { m[w.week_ending] = { work_start: w.work_start, work_end: w.work_end }; });
    return m;
  }, [weeksBreakdown]);

  const orderedWeeks = useMemo(() => (
    (weeksBreakdown || []).map((w) => w.week_ending).filter(Boolean).sort((a, b) => a.localeCompare(b))
  ), [weeksBreakdown]);

  const activeTeamNames = useMemo(() => (
    teams.map((t) => t.team_name)
      .filter((n) => selectedTeams.includes(n))
      .filter((n) => isAdmin || n !== 'PM1')
      .sort(sortTeamName)
  ), [teams, selectedTeams, isAdmin]);

  const perTeamWeekly = useMemo(() => {
    const out = {};
    teams.filter((t) => activeTeamNames.includes(t.team_name)).forEach((t) => {
      const map = {};
      buildTeamWeekly(t, weekMetaMap).forEach((w) => { map[w.week_ending] = w; });
      out[t.team_name] = map;
    });
    return out;
  }, [teams, activeTeamNames, weekMetaMap]);

  const metricObj = availableMetrics.find((m) => m.key === metric) || availableMetrics[0];
  const isMoney = metricObj.money;
  const fmt = (v) => (isMoney ? formatMoney(v) : v);
  const getVal = (teamName, wk) => {
    const w = perTeamWeekly[teamName] && perTeamWeekly[teamName][wk];
    return w ? (w[metric] || 0) : 0;
  };

  const totalsByTeam = {};
  activeTeamNames.forEach((n) => { totalsByTeam[n] = orderedWeeks.reduce((s, wk) => s + getVal(n, wk), 0); });

  const chartData = orderedWeeks.map((wk) => {
    const meta = weekMetaMap[wk] || {};
    const label = meta.work_start ? `${short(meta.work_start)}–${short(meta.work_end)}` : full(wk);
    const point = { week: label };
    activeTeamNames.forEach((n) => { point[n] = getVal(n, wk); });
    return point;
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[88vh] overflow-hidden flex flex-col" data-testid="crews-compare-teams-modal">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="text-xl font-bold text-indigo-800 flex items-center gap-2">
            🆚 Comparar Equipes (por semana)
          </DialogTitle>
          <p className="text-sm text-gray-600 mt-1">
            Comparação semana a semana das equipes selecionadas. Base: <b>Data de Pagamento</b>.
          </p>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 pr-2 space-y-4" style={{ WebkitOverflowScrolling: 'touch' }}>
          {activeTeamNames.length < 2 ? (
            <div className="text-center text-gray-400 py-10" data-testid="crews-compare-teams-need-2">
              Selecione pelo menos 2 equipes em "Selecionar Equipes para Comparar".
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 flex-wrap">
                <label className="text-sm font-medium text-gray-700">📈 Métrica:</label>
                <select
                  data-testid="crews-compare-teams-metric"
                  value={metric}
                  onChange={(e) => setMetric(e.target.value)}
                  className="border rounded px-3 py-2 text-sm"
                >
                  {availableMetrics.map((m) => (
                    <option key={m.key} value={m.key}>{m.label}</option>
                  ))}
                </select>
                <span className="text-xs text-gray-500">Escolha entre os campos marcados em "Customizar Campos".</span>
              </div>

              <div className="overflow-x-auto" data-testid="crews-compare-teams-table">
                <table className="w-full text-sm border rounded">
                  <thead>
                    <tr className="bg-indigo-50 text-indigo-900">
                      <th className="px-3 py-2 text-left whitespace-nowrap">Semana</th>
                      {activeTeamNames.map((n) => (
                        <th key={n} className="px-3 py-2 text-right border-l whitespace-nowrap">{n}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {orderedWeeks.map((wk) => {
                      const meta = weekMetaMap[wk] || {};
                      const label = meta.work_start ? `${short(meta.work_start)} – ${short(meta.work_end)}` : `Pgto ${full(wk)}`;
                      const vals = activeTeamNames.map((n) => getVal(n, wk));
                      const maxVal = Math.max(...vals);
                      return (
                        <tr key={wk} className="hover:bg-gray-50">
                          <td className="px-3 py-2 font-medium whitespace-nowrap">{label}</td>
                          {activeTeamNames.map((n, i) => {
                            const v = vals[i];
                            const isTop = maxVal > 0 && v === maxVal;
                            return (
                              <td key={n} className={`px-3 py-2 text-right border-l whitespace-nowrap ${isTop ? 'font-bold text-teal-700 bg-teal-50' : ''}`}>
                                {isTop ? '🥇 ' : ''}{fmt(v)}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-gray-100 font-bold border-t-2 border-gray-300">
                      <td className="px-3 py-2">TOTAL</td>
                      {activeTeamNames.map((n) => {
                        const maxTotal = Math.max(...activeTeamNames.map((x) => totalsByTeam[x]));
                        const isTop = maxTotal > 0 && totalsByTeam[n] === maxTotal;
                        return (
                          <td key={n} className={`px-3 py-2 text-right border-l whitespace-nowrap ${isTop ? 'text-teal-700' : ''}`}>
                            {isTop ? '🏆 ' : ''}{fmt(totalsByTeam[n])}
                          </td>
                        );
                      })}
                    </tr>
                  </tfoot>
                </table>
              </div>
              <p className="text-[11px] text-gray-500">🥇 = maior valor da semana · 🏆 = maior total do período.</p>

              {chartData.length > 1 && (
                <div className="bg-white border rounded-lg p-4 mt-2" data-testid="crews-compare-teams-chart">
                  <h4 className="font-semibold text-indigo-800 mb-3 text-sm">📈 Evolução por semana — {metricObj.label}</h4>
                  <div style={{ width: '100%', height: 300 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData} margin={{ top: 5, right: 20, bottom: 40, left: 10 }}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="week" angle={-35} textAnchor="end" height={60} style={{ fontSize: 11 }} />
                        <YAxis style={{ fontSize: 11 }} tickFormatter={(v) => (isMoney ? `$${(v / 1000).toFixed(0)}k` : v)} />
                        <Tooltip formatter={(v) => fmt(v)} />
                        <Legend />
                        {activeTeamNames.map((n, idx) => (
                          <Line key={n} type="monotone" dataKey={n} stroke={CHART_COLORS[idx % CHART_COLORS.length]} strokeWidth={2} dot={{ r: 3 }} />
                        ))}
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose} data-testid="crews-compare-teams-close">Fechar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default CompareTeamsModal;
